import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

/** Strip script/event handlers and javascript: to mitigate XSS when rendering SOP HTML */
function sanitizeSopHtml(html: string): string {
    if (!html) return '';
    let out = html
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '')
        .replace(/\s on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\s on\w+\s*=\s*[^\s>]+/gi, '')
        .replace(/javascript\s*:/gi, '');
    return out;
}

interface SopDialogProps {
    isOpen: boolean;
    onClose: () => void;
    sopName: string;
    sectionTitle: string;
    documentContent?: string;
    pdfUrl?: string; // absolute URL; will be fetched with auth and rendered inline
}

const SopDialog: React.FC<SopDialogProps> = ({
    isOpen,
    onClose,
    sopName,
    sectionTitle,
    documentContent,
    pdfUrl
}) => {
    const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);
	const [numPages, setNumPages] = useState<number>(0);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);

	// Configure PDF.js worker to load locally (avoids CDN issues)
	// pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

    useEffect(() => {
        let revoked = false;
        const loadPdf = async () => {
            if (!isOpen || !pdfUrl) {
                setPdfObjectUrl(null);
                setPdfLoading(false);
                setPdfError(null);
                return;
            }
            try {
                setPdfLoading(true);
                setPdfError(null);
                const token = localStorage.getItem('access_token');
                const res = await fetch(pdfUrl, {
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                });
                if (!res.ok) {
                    throw new Error(`Failed to load PDF (${res.status})`);
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                if (!revoked) setPdfObjectUrl(url);
            } catch (e: any) {
                setPdfError(e?.message || 'Failed to load PDF');
            } finally {
                setPdfLoading(false);
            }
        };
        loadPdf();

		// measure container width for responsive fit
		const updateWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.clientWidth);
			}
		};
		updateWidth();
		window.addEventListener('resize', updateWidth);
        return () => {
            revoked = true;
            if (pdfObjectUrl) {
                try { URL.revokeObjectURL(pdfObjectUrl); } catch { }
            }
			window.removeEventListener('resize', updateWidth);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, pdfUrl]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1060] sop-doc-popup" onClick={onClose}>
            <div className="max-w-4xl w-full max-h-[90vh] flex flex-col sop-doc-popup-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sop-doc-popup-header">
                    <div>
                        <p className="text-18-reg grey-50">📄 {sopName}</p>
                        <p className="text-14-reg grey-50 mt-1">Complete Document • Section: {sectionTitle}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full transition-colors"
                        aria-label="Close dialog"
                    >
                        <X className="w-5 h-5 text-grey-50" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {pdfUrl ? (
                        pdfLoading ? (
                            <div className="text-center text-gray-500 py-8">Loading PDF…</div>
                        ) : pdfError ? (
                            <div className="text-center text-red-600 py-8">{pdfError}</div>
                        ) : pdfObjectUrl ? (
						<div ref={containerRef} className="w-full max-h-[70vh] overflow-y-auto overflow-x-hidden border rounded flex justify-center bg-gray-50">
							{pdfObjectUrl && (
								<Document
									file={pdfObjectUrl}
									loading={<div className="text-center text-gray-500 py-8 w-full">Loading PDF…</div>}
									onLoadSuccess={({ numPages }: { numPages: number }) => setNumPages(numPages)}
									onLoadError={(err: Error) => setPdfError(err.message)}
									error={<div className="text-center text-red-600 py-8 w-full">Failed to render PDF.</div>}
								>
									<div className="flex flex-col items-center gap-4 py-6 w-full">
										{Array.from(new Array(numPages), (_el, index) => (
											<Page
												key={`page_${index + 1}`}
												pageNumber={index + 1}
												renderTextLayer={true}
												renderAnnotationLayer={true}
												width={Math.max(0, Math.min(containerWidth - 24, 1200))}
												className="shadow bg-white w-auto"
											/>
										))}
									</div>
								</Document>
							)}
						</div>
                        ) : (
                            <div className="text-center text-gray-500 py-8">PDF not available.</div>
                        )
                    ) : documentContent ? (
                        <div className="prose prose-sm max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: sanitizeSopHtml(documentContent) }} />
                        </div>
                    ) : (
                        <div className="text-center text-gray-500 py-8">
                            <p>Document content is not available.</p>
                            <p className="text-sm mt-2">Please contact your administrator for access to the full SOP document.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                            <img src="/logo.png" alt="Renegade" width={100}/>
                            <span>RENEGADE SERVICES</span>
                        </div>
                        <div className="text-right">
                            <p className="text-13-reg grey-600 mb-0">1937 WEST AVE LEVELLAND, TX 79336</p>
                            <p className="text-13-reg grey-600 mb-0">(806) 568-0042 | RENEGADEWLS.COM</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SopDialog;
